var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import * as queryString from 'query-string';
import { readCookie } from './simpleStore';
import { getTelemetryInstance } from './useTelemetry';
var consumerIdentified = false;
// Reduce references to window when possible
// via a getter to support SSR
var getAnalytics = function () { return window.analytics; };
/**
 * Retrieve all the DoorDash Common Fields from the browser cookies
 * and return them as an object.
 *
 * Common Fields are created by the backend and set as
 * cookies starting with 'dd_'.
 *
 * @returns {object} key-value representation of all
 * current common fields cookies
 */
var DD_COMMON_FIELDS = [
    'dd_login_id',
    'dd_district_id',
    'dd_zip_code',
    'dd_device_id',
    'dd_session_id',
    'dd_submarket_id',
    'dd_guest_id',
];
var getCommonFields = function () {
    return DD_COMMON_FIELDS.reduce(function (commonFields, fieldName) {
        var _a;
        return (__assign(__assign({}, commonFields), (_a = {}, _a[fieldName] = readCookie(fieldName), _a)));
    }, {});
};
var getBrowserData = function () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    var hasTouchEvents = window ? 'ontouchstart' in window : false;
    var innerWidth = (_a = window === null || window === void 0 ? void 0 : window.innerWidth) !== null && _a !== void 0 ? _a : 0;
    var innerHeight = (_b = window === null || window === void 0 ? void 0 : window.innerHeight) !== null && _b !== void 0 ? _b : 0;
    var userAgent = (_d = (_c = window === null || window === void 0 ? void 0 : window.navigator) === null || _c === void 0 ? void 0 : _c.userAgent) !== null && _d !== void 0 ? _d : 0;
    return {
        href: (_f = (_e = window === null || window === void 0 ? void 0 : window.location) === null || _e === void 0 ? void 0 : _e.href) !== null && _f !== void 0 ? _f : '',
        referrer: (_h = (_g = window === null || window === void 0 ? void 0 : window.document) === null || _g === void 0 ? void 0 : _g.referrer) !== null && _h !== void 0 ? _h : '',
        platform: hasTouchEvents && innerWidth < 900 ? 'mobile' : 'desktop',
        browser_width: innerWidth,
        browser_height: innerHeight,
        touch: hasTouchEvents,
        user_agent: userAgent,
    };
};
var getUtmFields = function () {
    try {
        var queryParams = queryString.parse(window.location.search) || {};
        return {
            utmCampaign: queryParams.utm_campaign,
            utmSource: queryParams.utm_source,
            utmMedium: queryParams.utm_medium,
        };
    }
    catch (err) {
        return {};
    }
};
var SEO_DOMAINS = [
    {
        domain: 'www.google.',
        utmAllowed: false,
    },
    {
        domain: 'com.google.',
        utmAllowed: false,
    },
    {
        domain: 'www.bing.',
        utmAllowed: false,
    },
    {
        domain: 'search.yahoo.',
        utmAllowed: true,
    },
    {
        domain: '.duckduckgo.',
        utmAllowed: true,
    },
    {
        domain: 'www.msn.',
        utmAllowed: true,
    },
];
export var isSeoVisitFunctional = function (referrer, queryParams, seoDomains) {
    var e_1, _a;
    try {
        for (var seoDomains_1 = __values(seoDomains), seoDomains_1_1 = seoDomains_1.next(); !seoDomains_1_1.done; seoDomains_1_1 = seoDomains_1.next()) {
            var seoDomain = seoDomains_1_1.value;
            var domain = seoDomain.domain, utmAllowed = seoDomain.utmAllowed;
            if (referrer.includes(domain)) {
                if (utmAllowed) {
                    return true;
                }
                return queryParams.utm_source === undefined;
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (seoDomains_1_1 && !seoDomains_1_1.done && (_a = seoDomains_1.return)) _a.call(seoDomains_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return false;
};
/**
 * Determine whether a user came to the landing page through SEO
 * by checking if the referrer url is Google or Bing with a utm_source param
 *
 * @returns {boolean} if user came from a SEO source
 */
export var isSeoVisit = function () {
    var referrer = (document.referrer || '').toLowerCase();
    var queryParams = queryString.parse(window.location.search) || {};
    return isSeoVisitFunctional(referrer, queryParams, SEO_DOMAINS);
};
export var identifyConsumer = function (consumerId) {
    if (getAnalytics() && !consumerIdentified) {
        consumerIdentified = true;
        getAnalytics().identify(consumerId);
    }
};
// Track and store custom event using Segment as destination.
// Check Segment configuration for more details:
// https://app.segment.com/doordash/sources/consumer_web/overview
var track = function (eventName, body, context, telemetryDidSend) {
    var _a;
    if (body === void 0) { body = {}; }
    var payload = __assign(__assign(__assign(__assign({}, body), getBrowserData()), getCommonFields()), getUtmFields());
    // uncomment to log warnings for all tracking events
    // console.warn('::: Tracking Event Fired :::', eventName, { payload, context })
    if (getAnalytics()) {
        getAnalytics().track(eventName, payload, context);
    }
    (_a = getTelemetryInstance()) === null || _a === void 0 ? void 0 : _a.event(eventName, payload).then(function () {
        telemetryDidSend === null || telemetryDidSend === void 0 ? void 0 : telemetryDidSend();
    }).catch(function () {
        telemetryDidSend === null || telemetryDidSend === void 0 ? void 0 : telemetryDidSend();
    });
};
/**
 * Wraps a passed function and callback, and allows user to be able to
 * invoke an event as they would originally, with the added side effect
 * of invoking the callback after a specific timeout.
 *
 * The motivation for this function is to be able to invoke analytics tracking
 * before hard page redirects to ensure that the tracking event
 * request completes
 * @param {function} trackingEvent - a tracking function to be invoked
 * @param {function} onTimeoutCb - callback invoked after a setTimeout
 * // Example usage:
 * const someAsyncCall = () => {
 *   console.log('async redirect')
 * }
 *
 * const handleTrackingWithTimeout = withTrackingTimeout(
 *   trackReferralLandingFAQClick,
 *   someAsyncCall
 * )
 * // This will now invoke the tracking call, and invoke callback after 300ms
 * handleTrackingWithTimeout()
 */
export var withTrackingTimeout = function (trackingEvent, onTimeoutCb) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        window.setTimeout(onTimeoutCb, 300);
        return trackingEvent.apply(null, args);
    };
};
export default track;
